/**
 * CensusRadioDetailsPanel — Panel lateral derecho con detalles de un radio censal 2022.
 *
 * @remarks
 * Se monta dentro de RightPanel cuando hay un radio seleccionado.
 * Muestra: código, ubicación, cobertura, indicadores ponderados.
 */

import type { SelectedCensusRadio } from './census-radio-layer-store';

// ─── Formateadores ────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return Math.round(n).toLocaleString('es-AR');
}

function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtKm2(m2: number | null | undefined): string {
  if (m2 === null || m2 === undefined) return '—';
  const km2 = m2 / 1_000_000;
  return `${km2.toFixed(4)} km²`;
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }): JSX.Element {
  return (
    <div className="crd-row">
      <span className="crd-label">{label}</span>
      <span className={`crd-value${accent ? ' crd-value--accent' : ''}`}>{value}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="crd-section-title">{children}</div>;
}

function Divider(): JSX.Element {
  return <div className="crd-divider" />;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface CensusRadioDetailsPanelProps {
  radio: SelectedCensusRadio;
  onClose: () => void;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function CensusRadioDetailsPanel({ radio, onClose }: CensusRadioDetailsPanelProps): JSX.Element {
  const pctNbi =
    radio.householdsTotalWeighted && radio.householdsTotalWeighted > 0
      ? (radio.householdsWithNbiWeighted ?? 0) / radio.householdsTotalWeighted
      : null;

  const coverageColor = radio.coverageRatio >= 0.999
    ? '#2FA4B5'
    : radio.coverageRatio >= 0.5
      ? '#f59e0b'
      : '#ef4444';

  return (
    <div className="crd-panel">
      {/* Header */}
      <div className="crd-header">
        <div className="crd-header-left">
          <span className="crd-title">Radio {radio.radioCode}</span>
          <span
            className="crd-coverage-badge"
            style={{ color: coverageColor, borderColor: coverageColor }}
          >
            {radio.isFull ? 'Completo' : `Parcial ${fmtPct(radio.coverageRatio)}`}
          </span>
        </div>
        <button className="crd-close" onClick={onClose} title="Cerrar">×</button>
      </div>

      <div className="crd-content">
        {/* Ubicación */}
        <SectionTitle>Ubicación</SectionTitle>
        <Row label="Departamento"  value={radio.departmentName ?? '—'} />
        <Row label="Provincia"     value={radio.provinceName   ?? '—'} />
        <Row label="Cobertura"     value={fmtPct(radio.coverageRatio)} accent />
        <Row label="Indicadores"   value={radio.hasIndicators ? 'Disponibles' : 'Sin indicadores'} />

        <Divider />

        {/* Población estimada */}
        <SectionTitle>Población estimada</SectionTitle>
        <Row label="Población total" value={fmt(radio.populationTotalWeighted)} accent />

        <Divider />

        {/* Hogares estimados */}
        <SectionTitle>Hogares estimados</SectionTitle>
        <Row label="Hogares totales" value={fmt(radio.householdsTotalWeighted)} accent />
        <Row label="Hogares con NBI" value={fmt(radio.householdsWithNbiWeighted)} />
        {pctNbi !== null && (
          <Row label="% NBI" value={`${(pctNbi * 100).toFixed(1)}%`} />
        )}

        <Divider />

        {/* Áreas */}
        <SectionTitle>Geometría</SectionTitle>
        <Row label="Área del radio"        value={fmtKm2(radio.radioAreaM2)} />
        <Row label="Área en el proyecto"   value={fmtKm2(radio.intersectionAreaM2)} />
      </div>
    </div>
  );
}
